import React, { useEffect, useState } from "react";
import Toastify, { notifyError, notifyInfo, notifySuccess } from "../../utils/tostfy";
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import {
  formatEmployeeIdForApi,
  normalizeEmployeeIdDigits,
  parseEmployeeIdDigits,
  sanitizeEmployeeIdInput,
} from "../../utils/employeeId";
import downArrow from "../../assets/image/down-arrow.svg";
import PhoneNumberField from "../../Components/PhoneNumberField/PhoneNumberField";
import { FIXED_COUNTRY_CODE } from "../../utils/phone";

const selectBaseClass =
  "md:h-[54px] h-[44px] w-full appearance-none rounded-3 border-[1px] bg-transparent px-3 pr-10 text-[15px] outline-none disabled:cursor-not-allowed disabled:opacity-70";

const SelectChevron = ({ className = "" }) => (
  <img
    src={downArrow}
    alt=""
    aria-hidden
    className={`pointer-events-none absolute top-1/2 h-[7px] w-[7px] -translate-y-1/2 shrink-0 ${className}`}
  />
);

const FormSelect = ({ className = "", wrapperClassName = "", hasError = false, children, ...props }) => (
  <div className={`relative ${wrapperClassName}`}>
    <select
      className={`${selectBaseClass} border-[#0B3E64] ${hasError ? "border-red-500" : ""} ${className}`}
      {...props}
    >
      {children}
    </select>
    <SelectChevron className="right-3" />
  </div>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_IN_RE = /^[6-9]\d{9}$/;
const PERMISSION_KEYS = ["add", "edit", "view",];

const initialErrors = {
  employee_id: "",
  full_name: "",
  email: "",
  mobile_number: "",
  mipl_location_id: "",
  permissions: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const privilegeKeyFromName = (name) => {
  const n = (name || "").toString().trim().toLowerCase();
  if (n === "add") return "add";
  if (n === "edit") return "edit";
  if (n === "view") return "view";
  if (n === "delete") return "delete";
  return null;
};

const extractModuleRows = (payload) => {
  const root = payload?.data ?? payload;
  const raw =
    root?.module_data ??
    root?.modules ??
    root?.module_list ??
    (Array.isArray(root) ? root : root?.data) ??
    null;
  const list = Array.isArray(raw) ? raw : toArray(raw);
  return list
    .map((m) => {
      const id = m?._id ?? m?.module_id ?? m?.id;
      const name =
        m?.module_name ?? m?.name ?? m?.title ?? m?.module_title ?? "Module";
      const privRaw =
        m?.privilege_type_data ??
        m?.privilege_id ??
        m?.privileges ??
        m?.privilege_list ??
        m?.privileges_data ??
        [];
      const privs = Array.isArray(privRaw) ? privRaw : toArray(privRaw);
      const privilegeIdsByKey = {};
      for (const p of privs) {
        const pid = p?._id ?? p?.privilege_id ?? p?.id;
        const typeLabel =
          p?.privilege_type ??
          p?.name ??
          p?.privilege_name ??
          p?.title ??
          p?.privilege ??
          "";
        const key = privilegeKeyFromName(typeLabel);
        if (key && pid) privilegeIdsByKey[key] = String(pid);
      }
      return {
        id: id ? String(id) : "",
        name: String(name),
        privilegeIdsByKey,
      };
    })
    .filter((row) => row.id && row.name);
};

const emptyAccess = () => ({
  all: false,
  add: false,
  edit: false,
  view: false,
  delete: false,
});

const pickSubAdmin = (d) => {
  if (!d || typeof d !== "object") return {};
  if (d.sub_admin && typeof d.sub_admin === "object") return d.sub_admin;
  if (d.subAdmin && typeof d.subAdmin === "object") return d.subAdmin;
  if (d.full_name != null || d.email != null || d.employee_id != null) return d;
  return d;
};

/** Collect granted privilege_type ids per module_id from view payload */
const collectPrivilegeIdsByModule = (sub) => {
  const map = new Map();
  const ingest = (blocks) => {
    if (!Array.isArray(blocks)) return;
    for (const block of blocks) {
      const midRaw = block?.module_id ?? block?.moduleId;
      const mid =
        midRaw && typeof midRaw === "object"
          ? midRaw._id ?? midRaw.id
          : midRaw;
      if (!mid) continue;
      const ids =
        block?.privilege_type_id ??
        block?.privilege_id ??
        [];
      const arr = Array.isArray(ids) ? ids : ids ? [ids] : [];
      const key = String(mid);
      if (!map.has(key)) map.set(key, new Set());
      arr.forEach((item) => {
        const pid =
          typeof item === "object" && item !== null
            ? item._id ?? item.id ?? item.privilege_id
            : item;
        if (pid != null && pid !== "") map.get(key).add(String(pid));
      });
    }
  };
  ingest(sub?.add_permissions);
  ingest(sub?.edit_permissions);
  ingest(sub?.delete_permissions);
  ingest(sub?.permissions);
  ingest(sub?.permission_data);
  ingest(sub?.assigned_permissions);
  return map;
};

const mergePrivilegesFromSubAdmin = (baseRows, sub) => {
  const byModule = collectPrivilegeIdsByModule(sub);
  return baseRows.map((row) => {
    const granted = byModule.get(row.id) || new Set();
    const access = { ...emptyAccess() };
    PERMISSION_KEYS.forEach((k) => {
      const pid = row.privilegeIdsByKey[k];
      access[k] = !!(pid && granted.has(pid));
    });
    const keys = PERMISSION_KEYS.filter((k) => row.privilegeIdsByKey[k]);
    const allChecked = keys.length > 0 && keys.every((k) => access[k]);
    return {
      ...row,
      access: { ...access, all: allChecked },
      /** Snapshot at load — used to detect removals on save */
      initialGranted: new Set(granted),
    };
  });
};

const mergeIntoBucket = (bucket, moduleId, privilegeTypeId) => {
  if (!moduleId || !privilegeTypeId) return;
  const mid = String(moduleId);
  let entry = bucket.find((x) => String(x.module_id) === mid);
  if (!entry) {
    entry = { module_id: mid, privilege_type_id: [] };
    bucket.push(entry);
  }
  if (!entry.privilege_type_id.includes(privilegeTypeId)) {
    entry.privilege_type_id.push(privilegeTypeId);
  }
};

/**
 * Delta vs initial grants:
 * - Newly checked Add/View → add_permissions; new Edit → edit_permissions
 * - Previously granted but now unchecked → delete_permissions (revoke)
 */
const buildEditPermissionBuckets = (moduleRows) => {
  const add_permissions = [];
  const edit_permissions = [];
  const delete_permissions = [];
  for (const row of moduleRows) {
    const mid = row.id;
    const initial = row.initialGranted || new Set();
    for (const key of PERMISSION_KEYS) {
      const pid = row.privilegeIdsByKey[key];
      if (!pid) continue;
      const wasGranted = initial.has(pid);
      const isChecked = !!row.access[key];
      if (wasGranted && !isChecked) {
        mergeIntoBucket(delete_permissions, mid, pid);
      } else if (!wasGranted && isChecked) {
        if (key === "add" || key === "view") {
          mergeIntoBucket(add_permissions, mid, pid);
        } else if (key === "edit") {
          mergeIntoBucket(edit_permissions, mid, pid);
        }
      }
    }
  }
  return { add_permissions, edit_permissions, delete_permissions };
};

const normalizeLocationIds = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((x) => {
        if (x == null) return "";
        if (typeof x === "object") return String(x._id ?? x.id ?? "");
        return String(x);
      })
      .filter(Boolean);
  }
  return [];
};

const EditSubAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const subAdminId = (searchParams.get("sub_admin_id") || "").trim();
  /** Prefill from list row (see SubAdminManagement → Edit link state) */
  const subAdminRecordFromList = location.state?.subAdminRecord;

  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [miplLocations, setMiplLocations] = useState([]);
  const [miplLocationIds, setMiplLocationIds] = useState([]);
  const [locationPicker, setLocationPicker] = useState("");
  const [moduleRows, setModuleRows] = useState([]);

  const [employeeId, setEmployeeId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    setSubheading("Edit Sub Admin");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    if (!subAdminId) {
      setPageLoading(false);
      return;
    }

    let cancelled = false;

    const applySubAdminDetail = (subRaw, baseRows) => {
      const sub = pickSubAdmin(subRaw);
      setEmployeeId(parseEmployeeIdDigits(sub.employee_id));
      setFullName((sub.full_name || sub.name || "").trim());
      setEmail((sub.email || "").trim());
      setMobileNumber(
        (sub.mobile_number ?? sub.mobile ?? "").toString().replace(/\D/g, "").slice(0, 10)
      );
      setMiplLocationIds(normalizeLocationIds(sub.mipl_location_id));
      setModuleRows(
        baseRows.length ? mergePrivilegesFromSubAdmin(baseRows, sub) : []
      );
    };

    const load = async () => {
      setPageLoading(true);
      try {
        const [modResp, locResp] = await Promise.all([
          handleApiCall("get", endpoint.listOfModule, {}),
          handleApiCall("get", endpoint.listOfAlllocation, {}),
        ]);

        if (cancelled) return;

        if (locResp?.data?.code === 200) {
          const d = locResp?.data?.data;
          const raw = Array.isArray(d) ? d : toArray(d?.location_data) || toArray(d);
          setMiplLocations(raw.map(mapLocation).filter((x) => x.id && x.name));
        } else {
          notifyError(
            locResp?.response?.data?.message ||
              locResp?.data?.message ||
              "Failed to load locations"
          );
          setMiplLocations([]);
        }

        if (modResp?.data?.code !== 200) {
          notifyError(
            modResp?.response?.data?.message ||
              modResp?.data?.message ||
              "Failed to load modules"
          );
          setModuleRows([]);
          if (subAdminRecordFromList) {
            applySubAdminDetail(subAdminRecordFromList, []);
          }
          return;
        }

        const baseRows = extractModuleRows(modResp.data).map((r) => ({
          ...r,
          access: emptyAccess(),
          initialGranted: new Set(),
        }));

        if (subAdminRecordFromList) {
          applySubAdminDetail(subAdminRecordFromList, baseRows);
        } else {
          setModuleRows(baseRows);
          notifyInfo(
            "Open Edit from the sub admin list to load saved profile and permissions (no detail API)."
          );
        }
      } catch {
        if (!cancelled) {
          notifyError("Failed to load page data");
          setModuleRows([]);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [subAdminId, subAdminRecordFromList?._id]);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const availableKeysForRow = (row) =>
    PERMISSION_KEYS.filter((k) => row.privilegeIdsByKey[k]);

  const syncAllFlag = (row, access) => {
    const keys = availableKeysForRow(row);
    const allChecked = keys.length > 0 && keys.every((k) => access[k]);
    return { ...access, all: allChecked };
  };

  const handlePermissionChange = (moduleId, type) => {
    clearFieldError("permissions");
    setModuleRows((prev) =>
      prev.map((row) => {
        if (row.id !== moduleId) return row;
        if (!row.privilegeIdsByKey[type]) return row;
        const next = {
          ...row.access,
          [type]: !row.access[type],
        };
        return {
          ...row,
          access: syncAllFlag(row, next),
        };
      })
    );
  };

  const handleAllChange = (moduleId) => {
    clearFieldError("permissions");
    setModuleRows((prev) =>
      prev.map((row) => {
        if (row.id !== moduleId) return row;
        const newValue = !row.access.all;
        const access = { ...emptyAccess(), all: newValue };
        availableKeysForRow(row).forEach((k) => {
          access[k] = newValue;
        });
        return {
          ...row,
          access: syncAllFlag(row, access),
        };
      })
    );
  };

  const hasAnyPermissionChecked = () => {
    for (const row of moduleRows) {
      for (const k of PERMISSION_KEYS) {
        if (row.access[k] && row.privilegeIdsByKey[k]) return true;
      }
    }
    return false;
  };

  const validate = () => {
    const next = { ...initialErrors };
    const id = normalizeEmployeeIdDigits(employeeId);
    const name = (fullName || "").trim();
    const em = (email || "").trim();
    const mobile = (mobileNumber || "").replace(/\D/g, "");

    if (!id) next.employee_id = "Employee ID is required";

    if (!name) next.full_name = "Name is required";
    else if (name.length < 2) next.full_name = "Name must be at least 2 characters";
    else if (name.length > 100) next.full_name = "Name is too long";
    else if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(name)) {
      next.full_name = "Enter a valid name";
    }

    if (!em) next.email = "Email is required";
    else if (!EMAIL_RE.test(em)) next.email = "Enter a valid email address";

    if (!mobile) next.mobile_number = "Phone number is required";
    else if (!MOBILE_IN_RE.test(mobile)) {
      next.mobile_number = "Enter a valid 10-digit Indian mobile number";
    }

    if (!miplLocationIds.length) {
      next.mipl_location_id = "Select at least one MIPL location";
    }

    if (!hasAnyPermissionChecked()) {
      next.permissions = "Select at least one permission";
    }

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!subAdminId) {
      notifyError("Sub admin ID is missing");
      return;
    }
    if (!validate()) return;

    const { add_permissions, edit_permissions, delete_permissions } =
      buildEditPermissionBuckets(moduleRows);

    setSubmitting(true);
    try {
      const payload = {
        sub_admin_id: subAdminId,
        employee_id: formatEmployeeIdForApi(normalizeEmployeeIdDigits(employeeId)),
        full_name: (fullName || "").trim(),
        email: (email || "").trim().toLowerCase(),
        mobile_number: (mobileNumber || "").replace(/\D/g, ""),
        country_code: FIXED_COUNTRY_CODE,
        mipl_location_id: [...miplLocationIds],
        add_permissions,
        edit_permissions,
        delete_permissions,
      };

      const response = await handleApiCall("put", endpoint.editSubAdmin, payload);
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Sub admin updated successfully");
        setTimeout(() => navigate("/sub-admin-management"), 1200);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to update sub admin";
      notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const disabledForm = submitting || pageLoading;

  if (!subAdminId) {
    return (
      <>
        <div className="row col-md-12 bg-white mx-0 mt-6 p-4 shadow-sm font-UbuntuRegular">
          <p className="text-gray-500 mb-0">Missing sub admin ID. Open edit from the list.</p>
          <Link to="/sub-admin-management" className="btn btn-primary mt-3">
            Back to list
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
    <Toastify/>
      <form onSubmit={onSubmit} noValidate className="font-UbuntuRegular">
        <div className="row col-md-12 bg-white mx-0 mt-6 px-2 py-3 md:p-4 shadow-sm font-UbuntuRegular g-3">
          {pageLoading ? (
            <div className="col-md-12 mb-4">
              <p className="text-gray-500 mb-0">Loading…</p>
            </div>
          ) : null}
          <div className="col-lg-3 col-md-6 mb-0 min-w-0 mt-0 md:!mt-[1rem]">
            <label className="font-UbuntuMedium">Employee ID</label>
            <div
              className={`mt-2 flex w-full min-w-0 items-stretch overflow-hidden rounded-3 border-[1px] ${
                errors.employee_id ? "border-red-500" : "border-[#0B3E64]"
              }`}
            >
              <span
                className="flex shrink-0 items-center border-r border-[#0B3E64]/25 bg-[#f5f8fa] px-3 font-UbuntuMedium text-[#0B3E64]"
                aria-hidden
              >
                MI
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={5}
                placeholder="00000"
                className="min-w-0 flex-1 border-0 bg-transparent px-3 py-0 outline-none md:h-[54px] h-[44px]"
                value={employeeId}
                onChange={(e) => {
                  setEmployeeId(sanitizeEmployeeIdInput(e.target.value));
                  clearFieldError("employee_id");
                }}
                onBlur={() => {
                  const normalized = normalizeEmployeeIdDigits(employeeId);
                  if (normalized !== employeeId) setEmployeeId(normalized);
                }}
                disabled={disabledForm}
                aria-label="Employee ID number (5 digits)"
              />
            </div>
            {errors.employee_id ? (
              <p className="text-red-500 text-sm mt-1 mb-0">{errors.employee_id}</p>
            ) : null}
          </div>
          <div className="col-lg-3 col-md-6 mb-0 min-w-0">
            <label className="font-UbuntuMedium">Enter Name</label>
            <input
              type="text"
              className={`w-full md:h-[54px] h-[44px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
                errors.full_name ? "border-red-500" : ""
              }`}
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                clearFieldError("full_name");
              }}
              disabled={disabledForm}
              autoComplete="name"
            />
            {errors.full_name ? (
              <p className="text-red-500 text-sm mt-1 mb-0">{errors.full_name}</p>
            ) : null}
          </div>
          <div className="col-lg-3 col-md-6 mb-0 min-w-0">
            <label className="font-UbuntuMedium">Enter Email</label>
            <input
              type="email"
              className={`w-full md:h-[54px] h-[44px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
                errors.email ? "border-red-500" : ""
              }`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              disabled={disabledForm}
              autoComplete="email"
            />
            {errors.email ? (
              <p className="text-red-500 text-sm mt-1 mb-0">{errors.email}</p>
            ) : null}
          </div>
          <div className="col-lg-3 col-md-6 mb-0 min-w-0">
            <label className="font-UbuntuMedium">Phone Number</label>
            <div className="mt-2">
              <PhoneNumberField
                value={mobileNumber}
                onChange={(value) => {
                  setMobileNumber(value);
                  clearFieldError("mobile_number");
                }}
                disabled={disabledForm}
                hasError={!!errors.mobile_number}
              />
            </div>
            {errors.mobile_number ? (
              <p className="text-red-500 text-sm mt-1 mb-0">{errors.mobile_number}</p>
            ) : null}
          </div>
          <div className="col-md-8 md:mb-4 min-w-0">
            <label className="font-UbuntuMedium">MIPL Location(s)</label>
            <FormSelect
              wrapperClassName="mt-2 max-w-full"
              hasError={!!errors.mipl_location_id}
              value={locationPicker}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setLocationPicker("");
                  return;
                }
                if (!miplLocationIds.includes(v)) {
                  setMiplLocationIds((prev) => [...prev, v]);
                  clearFieldError("mipl_location_id");
                }
                setLocationPicker("");
              }}
              disabled={disabledForm || pageLoading}
            >
              <option value="">Select location to add</option>
              {miplLocations
                .filter((loc) => !miplLocationIds.includes(loc.id))
                .map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
            </FormSelect>
            <div className="flex flex-wrap gap-2 mt-3 min-h-[34px]">
              {miplLocationIds.map((id) => {
                const loc = miplLocations.find((l) => l.id === id);
                const label = loc?.name || id;
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-200 pl-3 pr-1 py-1 text-sm font-UbuntuRegular"
                  >
                    <span className="max-w-[320px] truncate" title={label}>
                      {label}
                    </span>
                    <button
                      type="button"
                      className="flex h-7 min-w-[28px] shrink-0 items-center justify-center rounded-full text-lg leading-none text-[#0B3E64] hover:bg-black/10"
                      disabled={disabledForm}
                      aria-label={`Remove ${label}`}
                      onClick={() => {
                        setMiplLocationIds((prev) => prev.filter((x) => x !== id));
                        clearFieldError("mipl_location_id");
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
            {errors.mipl_location_id ? (
              <p className="text-red-500 text-sm mt-2 mb-0">{errors.mipl_location_id}</p>
            ) : null}
          </div>
        </div>

        <div className="bg-white mt-4 common-table-wrapper m-0">
          <div className="table-responsive">
            <table>
              <thead>
                <tr className="cus">
                  <th>S.No.</th>
                  <th className="text-start">Section/Page</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody
                className="
                        [&_label]:inline-flex 
                        [&_label]:items-center 
                        [&_label]:justify-center 
                        md:[&_label]:min-w-[94px] 
                        [&_label]:min-w-[60px]
                        md:[&_label]:min-h-[34px] 
                        [&_label]:min-h-[28px]
                        [&_label]:bg-gray-200 
                        [&_label]:rounded-full 
                        max-[767px]:[&_label]:text-[10px]
                        [&_label]:cursor-pointer
                        [&_label:has(input:checked)]:!bg-primary
                        [&_label:has(input:checked)]:text-white
                        [&_label:has(input:disabled)]:opacity-40
                        [&_label:has(input:disabled)]:cursor-not-allowed
                        [&_input]:hidden"
              >
                {pageLoading && (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-gray-500">
                      Loading modules…
                    </td>
                  </tr>
                )}
                {!pageLoading &&
                  moduleRows.map((row, index) => {
                    const hasAnyPriv = availableKeysForRow(row).length > 0;
                    return (
                      <tr key={row.id} className="border-t text-center">
                        <td>{String(index + 1).padStart(2, "0")}</td>
                        <td className="text-start">{row.name}</td>
                        <td>
                          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                            <label>
                              All
                              <input
                                type="checkbox"
                                checked={row.access.all}
                                onChange={() => handleAllChange(row.id)}
                                disabled={!hasAnyPriv || disabledForm}
                              />
                            </label>
                            {PERMISSION_KEYS.map((key) => (
                              <label key={key}>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                                <input
                                  type="checkbox"
                                  checked={row.access[key]}
                                  onChange={() => handlePermissionChange(row.id, key)}
                                  disabled={!row.privilegeIdsByKey[key] || disabledForm}
                                />
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {!pageLoading && moduleRows.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-gray-500">
                      No modules to display
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {errors.permissions ? (
            <p className="text-red-500 text-sm px-4 pb-3 mb-0">{errors.permissions}</p>
          ) : null}
        </div>

        <div className="flex gap-x-3 items-center md:!my-4 col-md-12 bg-white mx-0 md:p-4 p-3 shadow-sm">
          <Link
            to="/sub-admin-management"
            className="btn btn-primary"
            onClick={(e) => submitting && e.preventDefault()}
          >
            Cancel
          </Link>
          <button type="submit" className="btn btn-danger md:min-w-32" disabled={disabledForm}>
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </>
  );
};

export default EditSubAdmin;
